'use strict'

import { useShopperCustomersMutation } from '@salesforce/commerce-sdk-react'
import { useCurrentCustomer } from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useSiteType from './use-site-type'
import useShipToCountry from './use-ship-to-country'

/**
 * Custom hook for updating customer profile fields related to consent.
 *
 * This hook provides functions to update the consent fields for a customer profile
 * in both EU and US contexts. It utilizes a mutation to update the customer data
 * and handles any errors that may occur during the update process.
 *
 * @returns {Object} An object containing methods to update consent fields:
 *
 * @property {Function} updateEUConsentFields - Updates the EU consent fields for a customer.
 *   @async
 *   @returns {Promise<boolean>} A promise that resolves to true if the update was successful, or false if an error occurred.
 *
 * @property {Function} updateUSConsentFields - Updates the US consent fields for a customer profile.
 *   @async
 *   @returns {Promise<boolean>} A promise that resolves to true if the update is successful, or false if an error occurs.
 */
export const useUpdateProfileFields = () => {
    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')
    const { data: customer } = useCurrentCustomer()
    const { customerId } = customer
    const { isOutlet } = useSiteType()
    const { shipToCountry } = useShipToCountry()

    return {
        /**
         * Updates the privacy fields for a customer, including marketing and sharing consents.
         * This function sends an asynchronous request to update the customer's consent preferences
         * and adjusts newsletter subscription based on the site type (OUTLET or full price).
         *
         * @async
         * @param {boolean} marketingConsent - Indicates whether the customer consents to marketing communications.
         * @param {boolean} shareConsent - Indicates whether the customer consents to sharing their information.
         * @returns {Promise<boolean>} - A promise that resolves to true if the update was successful, or false if an error occurred.
         *
         * @throws Will return false if the update operation fails.
         */
        async updatePrivacyFields(marketingConsent, shareConsent, marketingConsentChanged, shareConsentChanged) {
            try {
                let body = {
                    c_marketingConsent: marketingConsent,
                    c_shareConsent: shareConsent,
                }

                if (marketingConsentChanged) {
                    body.c_marketingConsentDate = new Date()
                }

                if (shareConsentChanged) {
                    body.c_shareConsentDate = new Date()
                }

                if (isOutlet) {
                    body.c_outletNewsletter = marketingConsent ? true : false
                } else {
                    body.c_fullPriceNewsletter = marketingConsent ? true : false
                }

                if (!customer?.c_marketingConsent && marketingConsent) {
                    body.c_newsletterCountryCode = shipToCountry?.id
                }

                await updateCustomerMutation.mutate({
                    parameters: { customerId },
                    body: body
                })

                return true
            } catch (e) {
                return false
            }
        }
    }
}
